import * as React from "react";
import { Formik, Form, Field } from "formik";
import mapValues from "lodash/mapValues";
import moment from "moment";
import styled, { css } from "styled-components";
import useProductName from "brand-customization/useProductName";
import NumberUtils from "util/NumberUtils";
import BackendTypes from "archive/logic/BackendTypes";
import { HelpBlock, Input } from "components/bootstrap";
import { FormikInput, FormSubmit, Section, Select } from "components/common";
import { defaultCompare as naturalSort } from "logic/DefaultCompare";
import useHistory from "routing/useHistory";
import useDataLakeConfigMutation from "data-lake/configurations/hooks/useDataLakeConfigMutation";
import DataLakeBackendWarning from "data-lake/data-lake-backend-config/DataLakeBackendWarning";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import DataLakeRoutes from "data-lake/Routes";
import { formatDuration, formatRetentionLabel, validateRetention } from "data-lake/logic/Utils";
import AppConfig from "util/AppConfig";
const isCloud = AppConfig.isCloud();
const defaultValues = {
  active_backend: null,
  iceberg_commit_interval: "PT1H",
  iceberg_target_file_size: 536870912,
  parquet_row_group_size: 134217728,
  parquet_page_size: 8192,
  journal_reader_batch_size: 500,
  optimize_job_enabled: true,
  optimize_job_interval: "PT1H",
  optimize_rewrite_manifests_enabled: true,
  optimize_delete_orphan_files_enabled: true,
  parallel_retrieval_enabled: true,
  retrieval_convert_threads: -1,
  retrieval_convert_batch_size: 1,
  retrieval_inflight_requests: 3,
  retrieval_bulk_batch_size: 2500,
  retention_time: "PT1D",
  size_metrics_collection_interval: "PT15M"
};
const StyledFormSubmit = styled(FormSubmit)(
  ({ theme }) => css`
    display: flex;
    justify-content: flex-end;
    margin-top: ${theme.spacings.md};
  `
);
const formatBytes = (value) => NumberUtils.formatBytes(value);
const isValidDuration = (value) => moment.duration(value).asMilliseconds() >= 36e3;
const validateDuration = (value) => !isValidDuration(value) && "Invalid duration.";
const formatBackendValidationErrors = (backendErrors) => {
  const backendErrorStrings = mapValues(backendErrors, (errorArray) => `${errorArray.join(" ")}`);
  return { ...backendErrorStrings };
};
const getBackendOptions = (backends) => backends.map((backend) => ({
  value: backend.id,
  label: `${backend.title} (${BackendTypes.getBackendType(backend.settings.type).label})`
})).sort((a, b) => naturalSort(a.label.toLowerCase(), b.label.toLowerCase()));
const DataLakeConfigForm = ({ config, backends, showActiveBackend }) => {
  const productName = useProductName();
  const { onUpdateDataLakeConfig } = useDataLakeConfigMutation();
  const history = useHistory();
  const sendTelemetry = useSendTelemetry();
  const sortedBackends = getBackendOptions(backends);
  const redirectToDataLake = () => history.push(DataLakeRoutes.ARCHIVE.LIST);
  const handleSubmit = (values, { setErrors }) => onUpdateDataLakeConfig({
    config: {
      ...values,
      retention_time: values.retention_time === "0" ? null : values.retention_time
    }
  }).then(() => {
    redirectToDataLake();
  }).catch((error) => {
    var _a, _b;
    if (typeof ((_b = (_a = error == null ? void 0 : error.additional) == null ? void 0 : _a.body) == null ? void 0 : _b.errors) === "object") {
      setErrors(formatBackendValidationErrors(error.additional.body.errors));
    }
  }).finally(() => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.DATALAKE.CONFIGURATION_SAVED, {
      app_pathname: "data-lake"
    });
  });
  return /* @__PURE__ */ React.createElement(Formik, { initialValues: config || defaultValues, onSubmit: handleSubmit }, ({ values, isSubmitting, setFieldValue, initialValues, isValid }) => /* @__PURE__ */ React.createElement(Form, null, /* @__PURE__ */ React.createElement("fieldset", { disabled: isCloud }, /* @__PURE__ */ React.createElement(Section, { title: "Configuration information" }, showActiveBackend && /* @__PURE__ */ React.createElement(Field, { name: "active_backend" }, ({ field: { name, value }, form: { touched, setFieldTouched } }) => /* @__PURE__ */ React.createElement(
    Input,
    {
      help: "Select the active backend. Note that changing active backend will entail deleting any data stored in the current backend.",
      id: "active-backend",
      label: "Active Backend"
    },
    /* @__PURE__ */ React.createElement(
      Select,
      {
        inputId: "active-backend",
        name: "active_backend",
        placeholder: "Select Active Backend",
        "aria-label": "Select Active Backend",
        options: sortedBackends,
        required: true,
        value,
        disabled: isCloud,
        onChange: (selected) => {
          setFieldTouched(name, true);
          setFieldValue(name, selected);
        }
      }
    ),
    /* @__PURE__ */ React.createElement(HelpBlock, null),
    touched[name] && value !== initialValues[name] && /* @__PURE__ */ React.createElement(DataLakeBackendWarning, null)
  )), /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "text",
      name: "iceberg_commit_interval",
      id: "iceberg-commit-interval",
      label: "Iceberg Commit Interval",
      help: /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("em", null, "Advanced Option."), " Select the Iceberg Commit Interval. This dictates the interval of the commit task which makes newly ingested data available for retrieval. Smaller values minimize", " ", productName, " memory and storage use for buffering. Larger values are more performant for retrieval."),
      addonAfter: formatDuration(values.iceberg_commit_interval),
      validate: validateDuration,
      required: true
    }
  ), /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "number",
      name: "iceberg_target_file_size",
      id: "iceberg-target-file-size",
      label: "Iceberg Target File Size",
      help: /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("em", null, "Advanced Option."), " Size of the Iceberg target file size. This dictates the maximum file size of a segment before merging. Smaller segments minimize ", productName, " memory and storage use for buffering. Larger values are more performant for retrieval."),
      addonAfter: formatBytes(values.iceberg_target_file_size),
      required: true
    }
  ), /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "number",
      name: "parquet_row_group_size",
      id: "parquet-row-group-size",
      label: "Iceberg Parquet Row Group Size",
      help: /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("em", null, "Advanced Option."), " Size of Parquet Row Groups. This dictates the maximum group size within a segment, which influences read speed. The value set should be a factor of the Iceberg Target File Size. Typically updated only when altering the Iceberg Target File Size."),
      addonAfter: formatBytes(values.parquet_row_group_size),
      required: true
    }
  ), /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "number",
      name: "parquet_page_size",
      id: "parquet-page-size",
      label: "Parquet Page Size",
      help: /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("em", null, "Advanced Option."), " Size of Parquet Page Size*. This dictates the maximum page size within a group, which influences read speed. The value set should be a factor of the Iceberg Target File Size. Typically updated only when altering the Iceberg Target File Size."),
      addonAfter: formatBytes(values.parquet_page_size),
      required: true
    }
  ), /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "number",
      name: "journal_reader_batch_size",
      id: "journal-reader-batch-size",
      label: "Journal Reader Batch Size",
      help: /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("em", null, "Advanced Option."), " Size of Journal Reader Batch *. Higher values will result in a higher throughout of messages written from the Data Lake Journal to the Data Lake, but also greater java heap memory usage."),
      required: true
    }
  )), /* @__PURE__ */ React.createElement(Section, { title: "Optimization settings" }, /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "checkbox",
      name: "optimize_job_enabled",
      id: "optimize-job-enabled",
      label: "Enable automatic optimization?",
      help: "Whether to automatically optimize the stored data."
    }
  ), /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "checkbox",
      name: "optimize_rewrite_manifests_enabled",
      id: "optimize-rewrite-manifests-enabled",
      label: "Enable manifest rewrite?",
      disabled: !values.optimize_job_enabled,
      help: `An optimization process that consolidates small manifest files. Temporarily disable this during troubleshooting if you need to reduce the ${productName} Service's heap memory usage, for example, when investigating out-of-memory errors.`
    }
  ), /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "checkbox",
      name: "optimize_delete_orphan_files_enabled",
      id: "optimize-delete-orphan-files-enabled",
      label: "Enable orphan file deletion?",
      disabled: !values.optimize_job_enabled,
      help: `An optimization process that deletes orphaned Parquet files, which can be left behind after errors or improper shutdowns. Temporarily disable this during troubleshooting if reducing the ${productName} Service's heap memory usage is necessary, for example, when investigating out-of-memory errors.`
    }
  ), /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "text",
      name: "optimize_job_interval",
      id: "optimize-job-interval",
      label: "Optimize Interval",
      disabled: !values.optimize_job_enabled,
      help: 'Interval of the optimization job.  (i.e. "P1D" for 1 day, "PT6H" for 6 hours).',
      addonAfter: formatDuration(values.optimize_job_interval),
      validate: validateDuration,
      required: true
    }
  ), /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "text",
      name: "size_metrics_collection_interval",
      id: "size-metrics-collection-interval",
      label: "Size Metrics Collection Interval",
      help: 'Interval for collecting Data Lake size metrics (per-stream and aggregate). Minimum 1 minute. (i.e. "PT15M" for 15 minutes).',
      addonAfter: formatDuration(values.size_metrics_collection_interval),
      validate: validateDuration,
      required: true
    }
  )), /* @__PURE__ */ React.createElement(Section, { title: "Retrieval settings" }, /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "checkbox",
      name: "parallel_retrieval_enabled",
      id: "parallel-retrieval-enabled",
      label: "Enable parallel retrieval?",
      help: "Parallel retrieval performs concurrent indexing requests, which can speed up retrieval but uses more resources. The remainder of the values in this section only apply when parallel retrieval is enabled."
    }
  ), /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "number",
      name: "retrieval_inflight_requests",
      id: "retrieval-inflight-requests",
      label: "Maximum number of concurrent indexing requests",
      disabled: !values.parallel_retrieval_enabled,
      help: 'This setting determines the number of concurrent batches sent for indexing at the same time. This number should take into account the number of primary shards the "Restored Archives" index set has configured.'
    }
  ), /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "number",
      name: "retrieval_bulk_batch_size",
      id: "retrieval-bulk-batch-size",
      label: "Number of messages per indexing request",
      disabled: !values.parallel_retrieval_enabled,
      help: "In conjunction with concurrent indexing requests, this setting controls how many messages are indexed at the same time and can be used to influence retrieval performance."
    }
  ), /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "number",
      name: "retrieval_convert_threads",
      id: "retrieval-convert-threads",
      label: "Number of threads used to convert messages",
      disabled: !values.parallel_retrieval_enabled,
      help: "Defaults to number of available CPUs (value: -1). Typically you do not need to change this setting."
    }
  ), /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "number",
      name: "retrieval_convert_batch_size",
      id: "retrieval-convert-batch-size",
      label: "Conversion batch size",
      disabled: !values.parallel_retrieval_enabled,
      help: "Size of the per-thread conversion batch, defaults to 1. Typically you do not need to change this setting."
    }
  )), /* @__PURE__ */ React.createElement(Section, { title: "Retention setting" }, /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "text",
      name: "retention_time",
      id: "retention-time",
      label: "Maximum number of days in the Data Lake",
      help: "Define how many days to keep archives before deleting them. When 0 or P0D is set, automatic deletion is disabled.",
      addonAfter: formatRetentionLabel(values.retention_time),
      validate: validateRetention
    }
  ))), !isCloud && /* @__PURE__ */ React.createElement(
    StyledFormSubmit,
    {
      submitButtonText: "Update configuration",
      submitLoadingText: "Updating...",
      isSubmitting,
      disabledSubmit: !isValid,
      isAsyncSubmit: true,
      onCancel: redirectToDataLake
    }
  )));
};
export default DataLakeConfigForm;
